import type {
  ProductAudienceItem,
  ProductDetailHdConfig,
  ProductOutcomeItem,
  ProductTrustChip,
} from "./product-detail-types";
import { buildProductTrustChips } from "./product-detail-types";

export const EDU_HERO_PRODUCT_ASSET = "/assets/products/edu/edu-dashboard-hero.png";
export const EDU_HERO_HUMAN_ASSET =
  "/assets/products/edu/hero/itaas-edu-students-laptop.png?v=3";

export const EDU_AUDIENCES: ProductAudienceItem[] = [
  {
    id: "direction",
    icon: "🏛️",
    titleFr: "Direction",
    titleEn: "Leadership",
    bodyFr: "Pilotage scolarité, absences et recouvrement depuis un tableau de bord unique.",
    bodyEn: "Run enrollment, attendance and fee recovery from one leadership dashboard.",
  },
  {
    id: "teachers",
    icon: "👩‍🏫",
    titleFr: "Enseignants",
    titleEn: "Teachers",
    bodyFr: "Appel, notes, cahier de texte et préparations dans l’espace « Mon travail ».",
    bodyEn: "Attendance, grades, class journal and lesson prep in the teacher workspace.",
  },
  {
    id: "students",
    icon: "🎓",
    titleFr: "Élèves",
    titleEn: "Students",
    bodyFr: "Portail élève (Standard+) pour suivre notes, absences et documents partagés.",
    bodyEn: "Student portal (Standard+) for grades, absences and shared documents.",
  },
  {
    id: "parents",
    icon: "👨‍👩‍👧",
    titleFr: "Parents",
    titleEn: "Parents",
    bodyFr: "Portail familles : notes, absences, frais et notifications SMS / email.",
    bodyEn: "Family portal: grades, absences, fees and SMS / email notifications.",
  },
  {
    id: "admin",
    icon: "🗂️",
    titleFr: "Administration",
    titleEn: "Administration",
    bodyFr: "Inscriptions, dossiers, certificats et export EDUSER sans double saisie.",
    bodyEn: "Enrollment, records, certificates and EDUSER export without double entry.",
  },
];

export const EDU_OUTCOMES: ProductOutcomeItem[] = [
  {
    id: "admin",
    icon: "⚡",
    titleFr: "Administration simplifiée",
    titleEn: "Simplified administration",
    bodyFr: "Centralisez inscriptions, notes, absences et frais sans multiplier les registres Excel.",
    bodyEn: "Centralize enrollment, grades, absences and fees without juggling Excel registers.",
  },
  {
    id: "followup",
    icon: "🔎",
    titleFr: "Suivi plus rapide",
    titleEn: "Faster follow-up",
    bodyFr: "Retrouvez l’information élève et pédagogique au même endroit, prête pour la direction.",
    bodyEn: "Find student and academic information in one place, ready for leadership.",
  },
  {
    id: "families",
    icon: "📣",
    titleFr: "Parents mieux informés",
    titleEn: "Better-informed parents",
    bodyFr: "Facilitez le suivi de la scolarité et des communications via portail et notifications.",
    bodyEn: "Make school progress and communications easier via portal and notifications.",
  },
  {
    id: "data",
    icon: "📊",
    titleFr: "Données structurées",
    titleEn: "Structured school data",
    bodyFr: "Conservez une information scolaire organisée, exploitable et exportable (EDUSER).",
    bodyEn: "Keep school data organized, actionable and exportable (EDUSER).",
  },
];

const EDU_TRUST_BASE: ProductTrustChip[] = [
  {
    id: "rdc",
    labelFr: "Conçu pour les établissements en RDC",
    labelEn: "Built for schools in the DRC",
  },
  {
    id: "local",
    labelFr: "Accompagnement ITaaS RDC",
    labelEn: "ITaaS RDC support",
  },
];

/** @deprecated Prefer buildProductTrustChips + EDU_HD_CONFIG */
export function eduTrustChips(input: {
  trialDays?: number;
  promoActive: boolean;
  promoBadgeFr?: string;
  promoBadgeEn?: string;
}): ProductTrustChip[] {
  return buildProductTrustChips(EDU_TRUST_BASE, input);
}

export const EDU_HD_CONFIG: ProductDetailHdConfig = {
  productImageSrc: EDU_HERO_PRODUCT_ASSET,
  productImageAlt: {
    fr: "Tableau de bord ITaaS Edu — vue finance et opérations scolaires",
    en: "ITaaS Edu dashboard — school finance and operations overview",
  },
  humanImageSrc: EDU_HERO_HUMAN_ASSET,
  humanImageAlt: {
    fr: "Deux élèves collaborant sur un ordinateur portable",
    en: "Two students collaborating on a laptop",
  },
  visualCaption: {
    fr: "Interface produit ITaaS Edu — tableau de bord de gestion scolaire",
    en: "ITaaS Edu product interface — school management dashboard",
  },
  audiences: EDU_AUDIENCES,
  audienceTitle: {
    fr: "Une solution complète pour chaque acteur de l’école",
    en: "A complete solution for every school stakeholder",
  },
  outcomes: EDU_OUTCOMES,
  outcomesLead: {
    fr: "Des bénéfices concrets pour le quotidien de l’établissement.",
    en: "Practical benefits for day-to-day school operations.",
  },
  contextTitle: {
    fr: "Aligné sur le fonctionnement scolaire en RDC",
    en: "Aligned with DRC school operations",
  },
  contextLead: {
    fr: "Pensé pour le vocabulaire scolaire local, les bulletins et les flux orientés EDUSER — sans sceau institutionnel non autorisé.",
    en: "Built for local school vocabulary, report cards, and EDUSER-oriented workflows — without unofficial seals.",
  },
  trustBaseChips: EDU_TRUST_BASE,
  showAmbassadorLink: true,
  ambassadorLabel: {
    fr: "Devenir ambassadeur Edu",
    en: "Become an Edu ambassador",
  },
  showEditionShowcase: true,
};

export {
  localizeAudience,
  localizeOutcome,
  type ProductAudienceItem,
  type ProductOutcomeItem,
  type ProductTrustChip,
} from "./product-detail-types";
